import React, { useEffect, useState } from "react";

import { isAxiosError } from "axios";
import isFunction from "lodash/isFunction";

import useGetTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useGetTask.v3";
import useGetTaskComments from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskComments";
import { TaskDetailsV3 } from "@fartherfinance/frontend/api/Tasks/Types";
import { TaskId } from "@fartherfinance/frontend/api/Types";

import { TASK_COMMENTS_STATIC_QUERY_PARAMS } from "../common";
import TaskDrawerFooter from "../TaskDrawerFooter/TaskDrawerFooter";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Text from "@src/sharedComponents/Typography/Text";

import TaskDetailsFormProviderV3 from "./Components/TaskDetailsFormProvider/TaskDetailsFormProviderV3";
import TaskDetailsUIV3 from "./Components/TaskDetailsUI/TaskDetailsUIV3";

import styles from "./TaskDetails.module.css";

interface TaskDetailsEmbeddedProps {
  taskId: TaskId;
  onTaskDetailsReady?: () => void;
  onReadyConditionCheck?: (taskDetails: TaskDetailsV3) => boolean;
}

const TaskDetailsEmbeddedV3: React.FC<TaskDetailsEmbeddedProps> = ({
  taskId,
  onTaskDetailsReady,
  onReadyConditionCheck,
}) => {
  const [hasTriggered, setHasTriggered] = useState(false);

  const auth = useAdvisorRequestAuth();
  const getTaskQueryV3 = useGetTaskV3(taskId, auth);
  const getTaskCommentsQuery = useGetTaskComments(
    taskId,
    TASK_COMMENTS_STATIC_QUERY_PARAMS,
    auth
  );

  const {
    data: taskDetails,
    isLoading: isTaskDetailsLoading,
    hasError: taskDetailsHasError,
  } = getTaskQueryV3;
  const {
    data: taskComments,
    isLoading: isTaskCommentsLoading,
    hasError: taskCommentsHasError,
  } = getTaskCommentsQuery;
  const { values: comments = [] } = taskComments ?? {};

  const isLoading = isTaskDetailsLoading || isTaskCommentsLoading;
  const hasError = taskDetailsHasError || taskCommentsHasError;

  const determineErrorMessage = (): string => {
    if (
      taskDetailsHasError &&
      isAxiosError(getTaskQueryV3.error) &&
      getTaskQueryV3.error.response?.status === 403
    ) {
      return "You don’t have access to this task.";
    }

    return "Something went wrong, please try again later.";
  };

  useEffect(() => {
    const conditionCheck =
      isFunction(onReadyConditionCheck) && taskDetails
        ? onReadyConditionCheck(taskDetails)
        : true;

    if (
      isFunction(onTaskDetailsReady) &&
      !isLoading &&
      !hasError &&
      !hasTriggered &&
      conditionCheck
    ) {
      onTaskDetailsReady();
      setHasTriggered(true);
    }

    return () => {
      if (hasTriggered) {
        setHasTriggered(false);
      }
    };
  }, [
    onTaskDetailsReady,
    onReadyConditionCheck,
    taskDetails,
    isLoading,
    hasError,
    hasTriggered,
  ]);

  return (
    <>
      {taskDetails && (
        <TaskDetailsFormProviderV3 taskDetails={taskDetails}>
          {taskDetails.isDeleted && <div className={styles.overlay} />}

          <div className={styles.embeddedContainer}>
            <TaskDetailsUIV3
              taskDetails={taskDetails}
              taskComments={comments}
              isEmbedded
            />

            <TaskDrawerFooter taskId={taskDetails.id} />
          </div>
        </TaskDetailsFormProviderV3>
      )}

      {hasError && (
        <div className={styles.errorContainer}>
          <Text variant="title2">{determineErrorMessage()}</Text>
        </div>
      )}

      {isLoading && (
        <div className={styles.loading}>
          <LogoLoadingStill />
        </div>
      )}
    </>
  );
};

export default TaskDetailsEmbeddedV3;
